import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import { DateOnly } from '@atlassian/jira-platform-utils-date-only';
import type { FullCalendarApi, FullCalendarEvent } from '../../types';
import type { CalendarViewRangeDateOnly } from './types';

/**
 * This is a dumb default way to get date range
 * Ideally we always want to get the current date range from the FullCalendar itself
 * but it will be tricky to get it in the first render since we need to access it from `ref`
 */
const getInitialDates = () => {
	const date = new Date();

	return {
		firstDayOfTheMonth: new Date(date.getFullYear(), date.getMonth(), 1),
		lastDayOfTheMonth: new Date(date.getFullYear(), date.getMonth() + 1, 0),
		// start day of the month - 14 days
		startDate: new Date(date.getFullYear(), date.getMonth(), 1 - 14),
		// last day of the month - 14 days
		endDate: new Date(date.getFullYear(), date.getMonth() + 1, 0 + 14),
	};
};

/**
 * This is a function to get fullCalendarApi,
 * it will return the range in UTC timezone, DIFFERENT to the public docs in https://fullcalendar.io/docs/timeZone
 * where the API is supposed to emit Date objects in local timezone
 *
 * This is because: the way to retrieve the calendar range is NOT a public API
 * And it's not ideal to do it this way
 * This will be fixed in https://jbusiness.atlassian.net/browse/JET-279
 *
 * @returns currentRange and activeRange of the calendar in UTC timezone
 */
export const getFullCalendarDateProfile = (fullCalendarApi: FullCalendarApi) => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	const { currentRange, activeRange } = (fullCalendarApi as any).getCurrentData().dateProfile;

	return {
		currentRange,
		activeRange,
	};
};

export const getInitialCalendarViewRangeDateOnly = (): CalendarViewRangeDateOnly => {
	const { firstDayOfTheMonth, lastDayOfTheMonth, endDate, startDate } = getInitialDates();
	return {
		currentRange: {
			start: DateOnly.fromDateAccordingToLocalTime(firstDayOfTheMonth),
			end: DateOnly.fromDateAccordingToLocalTime(lastDayOfTheMonth),
		},
		activeRange: {
			start: DateOnly.fromDateAccordingToLocalTime(startDate),
			end: DateOnly.fromDateAccordingToLocalTime(endDate),
		},
	};
};

export const monthsBetweenCurrentDateOnly = (dateOnly: DateOnly) => {
	const today = new Date();
	const date = dateOnly.toLocalDate();
	return date.getMonth() - today.getMonth() + 12 * (date.getFullYear() - today.getFullYear());
};

const getIssue = (event: FullCalendarEvent) => event?.extendedProps?.issue;
const isPlaceholder = (event: FullCalendarEvent) =>
	getIssue(event) == null && !isReleaseEvent(event);
const isOptimistic = (event: FullCalendarEvent) => getIssue(event) && !getIssue(event)?.key;
const isIssueEvent = (event: FullCalendarEvent) => Boolean(getIssue(event)?.key);
const isReleaseEvent = (event: FullCalendarEvent) => Boolean(event?.extendedProps?.release);
const getCalendarEventLengthInCalendarDays = (event: FullCalendarEvent) => {
	const issue = getIssue(event);
	return issue?.duedate && issue?.startdate
		? // Note that differenceInCalendarDays discards time component of dates.
			differenceInCalendarDays(
				// Add a day to the due date so that we include the full day of the due date, otherwise an event spanning Monday and Tuesday will be treated as one calendar day.
				addDays(issue?.duedate?.toUTCDate(), 1),
				issue?.startdate?.toUTCDate(),
			)
		: 0;
};

// eslint-disable-next-line complexity
export const calendarEventOrderSort = (a: FullCalendarEvent, b: FullCalendarEvent) => {
	// Move up if the item is a placeholder and comparison item is an issue.
	if (isPlaceholder(a) && (isIssueEvent(b) || isReleaseEvent(b))) {
		return -1;
	}
	// Move down if the item is an issue and comparison item is a placeholder.
	if (isPlaceholder(b) && (isIssueEvent(a) || isReleaseEvent(a))) {
		return 1;
	}

	// If this is a release and comparison item is a multi-day issue or optimistic item, move down, otherwise move up.
	if (isReleaseEvent(a) && !isReleaseEvent(b)) {
		return getCalendarEventLengthInCalendarDays(b) > 1 ? 1 : -1;
	}

	// If this is a multi-day issue and comparison item is a release or optimistic item, move up, otherwise move down.
	if (isReleaseEvent(b) && !isReleaseEvent(a)) {
		return getCalendarEventLengthInCalendarDays(a) > 1 ? -1 : 1;
	}

	if (isReleaseEvent(a) && isReleaseEvent(b)) {
		return 0;
	}

	// Sort issues and optimistic item. Optimistic item position always matches position of corresponding real issue created.
	// Sort multi-day issues by length descending.
	if (getCalendarEventLengthInCalendarDays(a) > 1 || getCalendarEventLengthInCalendarDays(b) > 1) {
		const daysDifference =
			getCalendarEventLengthInCalendarDays(b) - getCalendarEventLengthInCalendarDays(a);
		if (daysDifference < 0) {
			return -1;
		}
		if (daysDifference > 0) {
			return 1;
		}
	}
	// Sort issue by start ascending.
	if (a?.start && b?.start) {
		const timeDifference = new Date(a.start).getTime() - new Date(b.start).getTime();
		if (timeDifference < 0) {
			return -1;
		}
		if (timeDifference > 0) {
			return 1;
		}
	}
	// Sort issue by title descending.
	if (a?.title && b?.title) {
		// Numeric option required as localeCompare sorts in lexicographical order by default.
		return b.title.localeCompare(a.title, undefined, { numeric: true });
	}

	// Sort optimistic item same as by title if above comparisons were not enough to sort and since optimistic item does not have a title.
	if (isOptimistic(a) && !isOptimistic(b)) {
		// Move optimistic item up if comparison item is an issue.
		return -1;
	}
	if (isOptimistic(b) && !isOptimistic(a)) {
		// Move issue down if comparison item is an optimistic item.
		return 1;
	}

	return 0;
};
